import { map } from 'rxjs';
import { pageGroup } from './core/permissions';
import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { SuperAdminGuard } from './guards/super-admin.guard';
import { OrderAcceptingGuard } from './guards/order-accepting.guard';
import { permissionGuard } from './guards/permission.guard';
import { AuthService } from './services/auth.service';
import { outletProfileGuard } from './services/outlet-profile-session.service';

export const routes: Routes = [
  {
    // The marketing site lives on its own now, so this app is the partner
    // panel and nothing else: the root goes straight to the sign-in page.
    // about-us, contact-us, privacy-policy and terms-of-service stay
    // reachable below - they are linked from emails and the app stores.
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    // Self-sign-up is closed (logins are created by FoodCiti).
    path: 'register',
    redirectTo: 'login'
  },
  {
    path: 'about-us',
    loadComponent: () => import('./pages/landing/about-us/about-us.component').then(m => m.AboutUsComponent)
  },
  {
    path: 'contact-us',
    loadComponent: () => import('./pages/landing/contact-us/contact-us.component').then(m => m.ContactUsComponent)
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/landing/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)
  },
  {
    path: 'terms-of-service',
    loadComponent: () => import('./pages/landing/terms-of-service/terms-of-service.component').then(m => m.TermsOfServiceComponent)
  },
  {
    // Every child carries the area it belongs to in `data.permission`, checked
    // by `permissionGuard`; pages that are FoodCiti's own keep SuperAdminGuard.
    // Typing a forbidden URL lands on the first page the login may open.
    path: 'admin',
    loadComponent: () => import('./layouts/admin-layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    canActivate: [AuthGuard],
    children: [
      {
        path: 'audit-checklists',
        canActivate: [SuperAdminGuard],
        loadChildren: () => import('./features/audit-checklists/audit-checklists.routes').then(m => m.AUDIT_CHECKLIST_ROUTES)
      },
      {
        // Worked out per login: a staff login without the Dashboard area
        // cannot be sent to the dashboard.
        path: '',
        pathMatch: 'full',
        redirectTo: () => { const auth = inject(AuthService); return auth.refreshPermissions().pipe(map(() => auth.firstAllowedUrl())); }
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/admin/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'dashboard' }
      },
      {
        path: 'no-access',
        loadComponent: () => import('./pages/admin/no-access/no-access.component').then(m => m.NoAccessComponent)
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/admin/settings/settings.component').then(m => m.SettingsComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/payments',
        loadComponent: () => import('./pages/admin/settings/payments/payment-settings.component').then(m => m.PaymentSettingsComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/restaurants',
        loadComponent: () => import('./pages/admin/settings/restaurants/restaurants.component').then(m => m.RestaurantsComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/businesses',
        loadComponent: () => import('./pages/admin/settings/businesses/business-list/business-list.component').then(m => m.BusinessListComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/businesses/new',
        loadComponent: () => import('./pages/admin/settings/businesses/business-wizard/business-wizard.component').then(m => m.BusinessWizardComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/businesses/:id',
        loadComponent: () => import('./pages/admin/settings/businesses/business-detail/business-detail.component').then(m => m.BusinessDetailComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        // Two screens, not two tabs: panel modules decide what RBAC can hand
        // out, app modules are read by the mobile app and the storefront.
        path: 'settings/modules',
        loadComponent: () => import('./pages/admin/settings/modules/modules.component').then(m => m.ModulesComponent),
        canActivate: [SuperAdminGuard],
        data: { side: 'panel' }
      },
      {
        path: 'settings/app-modules',
        loadComponent: () => import('./pages/admin/settings/modules/modules.component').then(m => m.ModulesComponent),
        canActivate: [SuperAdminGuard],
        data: { side: 'app' }
      },
      {
        path: 'settings/users',
        loadComponent: () => import('./pages/admin/settings/users/users.component').then(m => m.UsersComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'outlet.users' }
      },
      {
        path: 'settings/print-settings',
        loadComponent: () => import('./pages/admin/settings/print-settings/print-settings.component').then(m => m.PrintSettingsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'outlet.printSettings' }
      },
      {
        path: 'settings/pos-devices',
        loadComponent: () => import('./pages/admin/settings/pos-devices/pos-devices.component').then(m => m.PosDevicesComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'outlet.pos' }
      },
      {
        path: 'settings/sms-providers',
        loadComponent: () => import('./pages/admin/settings/sms-providers/sms-providers.component').then(m => m.SmsProvidersComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/email-providers',
        loadComponent: () => import('./pages/admin/settings/email-providers/email-providers.component').then(m => m.EmailProvidersComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/whatsapp',
        loadComponent: () => import('./pages/admin/settings/whatsapp/whatsapp.component').then(m => m.WhatsAppComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/logs',
        loadComponent: () => import('./pages/admin/settings/logs/logs-hub.component').then(m => m.LogsHubComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'settings/logs/whatsapp',
        loadComponent: () => import('./pages/admin/settings/logs/whatsapp-logs/whatsapp-logs.component').then(m => m.WhatsAppLogsComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'my-restaurant',
        loadComponent: () => import('./pages/admin/my-restaurant/my-restaurant.component').then(m => m.MyRestaurantComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        canDeactivate: [outletProfileGuard],
        data: { permission: 'outlet.details' }
      },
      {
        path: 'my-restaurant/:id',
        loadComponent: () => import('./pages/admin/my-restaurant/my-restaurant.component').then(m => m.MyRestaurantComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        canDeactivate: [outletProfileGuard],
        data: { fromSettings: true, permission: 'outlet.details' }
      },
      {
        path: 'menu',
        loadComponent: () => import('./pages/admin/menu/menu.component').then(m => m.MenuComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.categories' }
      },
      {
        path: 'kitchens',
        loadComponent: () => import('./pages/admin/menu/kitchens/kitchens.component').then(m => m.KitchensComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'outlet.kitchens' }
      },
      {
        path: 'inventory',
        loadComponent: () => import('./pages/admin/inventory/inventory-workspace.component').then(m => m.InventoryWorkspaceComponent),
        loadChildren: () => import('./pages/admin/inventory/inventory.routes').then(m => m.INVENTORY_ROUTES),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: [...pageGroup('manageStocks'), ...pageGroup('inventoryAdmin')].map(page => page.key) }
      },
      {
        path: 'menu/bulk-edit',
        loadComponent: () => import('./pages/admin/menu/bulk-edit-items/bulk-edit-items.component').then(m => m.BulkEditItemsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.categories' }
      },
      {
        path: 'menu/category/:id',
        loadComponent: () => import('./pages/admin/menu/category-detail/category-detail.component').then(m => m.CategoryDetailComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.categories' }
      },
      {
        path: 'menu/item/:id',
        loadComponent: () => import('./pages/admin/menu/item-editor/item-editor.component').then(m => m.ItemEditorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.categories' }
      },
      {
        path: 'menu/option-groups',
        loadComponent: () => import('./pages/admin/menu/option-groups/option-groups.component').then(m => m.OptionGroupsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.options' }
      },
      {
        path: 'menu/option-groups/create',
        loadComponent: () => import('./pages/admin/menu/option-group-editor/option-group-editor.component').then(m => m.OptionGroupEditorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.options' }
      },
      {
        path: 'menu/option-groups/edit/:id',
        loadComponent: () => import('./pages/admin/menu/option-group-editor/option-group-editor.component').then(m => m.OptionGroupEditorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.options' }
      },
      {
        path: 'menu/modifier-groups',
        loadComponent: () => import('./pages/admin/menu/option-modifiers/option-modifiers.component').then(m => m.OptionModifiersComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.options' }
      },
      {
        path: 'offers',
        loadComponent: () => import('./pages/admin/offers/offers-list/offers-list.component').then(m => m.OffersListComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.offers' }
      },
      {
        path: 'offers/create',
        loadComponent: () => import('./pages/admin/offers/offer-creator/offer-creator.component').then(m => m.OfferCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.offers' }
      },
      {
        path: 'offers/edit/:id',
        loadComponent: () => import('./pages/admin/offers/offer-creator/offer-creator.component').then(m => m.OfferCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.offers' }
      },
      {
        path: 'coupons',
        loadComponent: () => import('./pages/admin/coupons/coupons-list/coupons-list.component').then(m => m.CouponsListComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.coupons' }
      },
      {
        path: 'coupons/create',
        loadComponent: () => import('./pages/admin/coupons/coupon-creator/coupon-creator.component').then(m => m.CouponCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.coupons' }
      },
      {
        path: 'coupons/edit/:id',
        loadComponent: () => import('./pages/admin/coupons/coupon-creator/coupon-creator.component').then(m => m.CouponCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.coupons' }
      },
      {
        path: 'meal-deals',
        loadComponent: () => import('./pages/admin/meal-deals/meal-deal-list/meal-deal-list.component').then(m => m.MealDealListComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.mealDeals' }
      },
      {
        path: 'meal-deals/create',
        loadComponent: () => import('./pages/admin/meal-deals/meal-deal-creator/meal-deal-creator.component').then(m => m.MealDealCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.mealDeals' }
      },
      {
        path: 'meal-deals/edit/:id',
        loadComponent: () => import('./pages/admin/meal-deals/meal-deal-creator/meal-deal-creator.component').then(m => m.MealDealCreatorComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'menu.mealDeals' }
      },
      {
        path: 'table-booking',
        loadComponent: () => import('./pages/admin/table-booking/table-booking.component').then(m => m.TableBookingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'tables.overview' }
      },
      {
        path: 'table-booking/diary',
        loadComponent: () => import('./pages/admin/table-booking/diary/diary.component').then(m => m.DiaryComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'tables.diary' }
      },
      {
        path: 'table-booking/floor-management',
        loadComponent: () => import('./pages/admin/table-booking/floor-management/floor-management.component').then(m => m.FloorManagementComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'tables.floors' }
      },
      {
        path: 'table-booking/settings',
        loadComponent: () => import('./pages/admin/table-booking/settings/table-booking-settings.component').then(m => m.TableBookingSettingsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'tables.settings' }
      },
      {
        path: 'orders', pathMatch: 'full', redirectTo: '/admin/reports/sales?report=sales-orders&period=today'
      },
      {
        path: 'analytics',
        loadComponent: () => import('./pages/admin/admin-analytics/admin-analytics.component').then(m => m.AdminAnalyticsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'reports.sales' }
      },
      {
        path: 'marketing', pathMatch: 'full',
        redirectTo: () => {
          const auth = inject(AuthService);
          return auth.refreshPermissions().pipe(map(() => {
            const page = pageGroup('marketing').find(item => item.url && auth.can(item.key));
            return page?.url || auth.firstAllowedUrl();
          }));
        }
      },
      {
        path: 'marketing/whatsapp',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'marketing.whatsapp', marketingPage: 'campaigns' }
      },
      {
        path: 'marketing/whatsapp/new',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always', canDeactivate: [outletProfileGuard],
        data: { permission: 'marketing.whatsapp', marketingPage: 'campaign' }
      },
      {
        path: 'marketing/whatsapp/:id/runs/:runId',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'marketing.whatsapp', marketingPage: 'report' }
      },
      {
        path: 'marketing/whatsapp/:id',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always', canDeactivate: [outletProfileGuard],
        data: { permission: 'marketing.whatsapp', marketingPage: 'campaign' }
      },
      {
        path: 'marketing/creatives',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always', canDeactivate: [outletProfileGuard],
        data: { permission: 'marketing.creatives', marketingPage: 'creatives' }
      },
      {
        path: 'marketing/brand',
        loadComponent: () => import('./pages/admin/marketing/marketing.component').then(m => m.MarketingComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always', canDeactivate: [outletProfileGuard],
        data: { permission: 'marketing.brand', marketingPage: 'brand' }
      },
      {
        path: 'reports',
        loadComponent: () => import('./pages/admin/reports/reports.component').then(m => m.ReportsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: pageGroup('reporting').map(page => page.key) }
      },
      {
        path: 'reports/:category',
        loadComponent: () => import('./pages/admin/reports/reports.component').then(m => m.ReportsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: pageGroup('reporting').map(page => page.key) }
      },
      {
        // Platform-wide customer records, not one restaurant's: FoodCiti only.
        path: 'customers',
        loadComponent: () => import('./pages/admin/admin-customers/admin-customers.component').then(m => m.AdminCustomersComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management',
        loadComponent: () => import('./pages/admin/media-management/media-management.component').then(m => m.MediaManagementComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management/menu',
        loadComponent: () => import('./pages/admin/media-management/menu-media/menu-media.component').then(m => m.MenuMediaComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management/prompt-manager',
        loadComponent: () => import('./pages/admin/media-management/prompt-manager/prompt-manager.component').then(m => m.PromptManagerComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management/special-occasion',
        loadComponent: () => import('./pages/admin/media-management/special-occasion/special-occasion.component').then(m => m.SpecialOccasionComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management/meal-deal',
        loadComponent: () => import('./pages/admin/media-management/meal-deal-media/meal-deal-media.component').then(m => m.MealDealMediaComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'media-management/offer',
        loadComponent: () => import('./pages/admin/media-management/offer-media/offer-media.component').then(m => m.OfferMediaComponent),
        canActivate: [SuperAdminGuard]
      },
      {
        path: 'notifications',
        loadComponent: () => import('./pages/admin/notifications/notifications.component').then(m => m.NotificationsComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: 'outlet.details' }
      },
      {
        path: 'legal',
        loadComponent: () => import('./pages/admin/legal/legal.component').then(m => m.LegalComponent),
        canActivate: [permissionGuard], runGuardsAndResolvers: 'always',
        data: { permission: ['legal.privacy', 'legal.terms'] }
      }
    ]
  },
  {
    path: 'restaurant/:id/privacy-policy',
    loadComponent: () => import('./pages/customer/restaurant-legal/restaurant-legal.component').then(m => m.RestaurantLegalComponent)
  },
  {
    path: 'restaurant/:id/terms',
    loadComponent: () => import('./pages/customer/restaurant-legal/restaurant-legal.component').then(m => m.RestaurantLegalComponent)
  },
  {
    path: 'restaurant/:slug/:id/privacy-policy',
    loadComponent: () => import('./pages/customer/restaurant-legal/restaurant-legal.component').then(m => m.RestaurantLegalComponent)
  },
  {
    path: 'restaurant/:slug/:id/terms',
    loadComponent: () => import('./pages/customer/restaurant-legal/restaurant-legal.component').then(m => m.RestaurantLegalComponent)
  },
  {
    path: 'restaurant/:id/reviews',
    loadComponent: () => import('./pages/customer/restaurant-reviews/restaurant-reviews.component').then(m => m.RestaurantReviewsComponent)
  },
  {
    path: 'restaurant/:slug/:id/reviews',
    loadComponent: () => import('./pages/customer/restaurant-reviews/restaurant-reviews.component').then(m => m.RestaurantReviewsComponent)
  },
  {
    path: 'restaurant/:slug/:id',
    loadComponent: () => import('./pages/customer/restaurant-page/restaurant-page.component').then(m => m.RestaurantPageComponent)
  },
  {
    path: 'restaurant/:id',
    loadComponent: () => import('./pages/customer/restaurant-page/restaurant-page.component').then(m => m.RestaurantPageComponent)
  },
  {
    path: 'restaurant-mobile/:id/track/:orderId',
    loadComponent: () => import('./pages/customer/restaurant-page-mobile/order-tracking-mobile.component').then(m => m.OrderTrackingMobileComponent)
  },
  {
    path: 'restaurant-mobile/:id',
    loadComponent: () => import('./pages/customer/restaurant-page-mobile/restaurant-page-mobile.component').then(m => m.RestaurantPageMobileComponent)
  },
  {
    // Customer order page from the WhatsApp magic link: ordrciti.in/i/<code>
    path: 'i/:code',
    loadComponent: () => import('./pages/customer/order-link/order-link.component').then(m => m.OrderLinkComponent)
  },
  {
    path: 'feedback/:token',
    loadComponent: () => import('./pages/customer/feedback-page/feedback-page.component').then(m => m.FeedbackPageComponent)
  },
  {
    path: 'uploadimage',
    loadComponent: () => import('./pages/upload-image/upload-image.component').then(m => m.UploadImageComponent)
  },
  {
    path: 'orderaccepting/login',
    loadComponent: () => import('./pages/order-accepting/login/order-accepting-login.component').then(m => m.OrderAcceptingLoginComponent)
  },
  {
    path: 'orderaccepting',
    loadComponent: () => import('./pages/order-accepting/main/order-accepting.component').then(m => m.OrderAcceptingComponent),
    canActivate: [OrderAcceptingGuard]
  },
  {
    path: ':slug',
    loadComponent: () => import('./pages/customer/slug-resolver/slug-resolver.component').then(m => m.SlugResolverComponent)
  },
  {
    path: '**',
    redirectTo: '/'
  }
];
